// Intent: goals looked text-only — owner wants each goal to carry a typical
// icon, picked at creation/edit (mock: edycja_celu). Ionicons ONLY (never emoji;
// see no-emoji rule) — the vector set matches the Stitch design language.

import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { AppText } from '@/components/ui/app-text';
import { colors, radius, spacing } from '@/theme/tokens';
import type { SavingsGoal } from '@/types/domain';

type IconName = keyof typeof Ionicons.glyphMap;

/** 16 tiles per the Stitch mock (row-by-row: dom, auto, podróż, poduszka…). */
export const GOAL_ICONS: IconName[] = [
  'home-outline',
  'car-sport-outline',
  'airplane-outline',
  'umbrella-outline',
  'medkit-outline',
  'school-outline',
  'gift-outline',
  'heart-outline',
  'cash-outline',
  'shield-checkmark-outline',
  'bag-handle-outline',
  'paw-outline',
  'game-controller-outline',
  'bicycle-outline',
  'restaurant-outline',
  'sparkles-outline',
];

const TEMPLATE_ICONS: Record<SavingsGoal['template'], IconName> = {
  DOWN_PAYMENT: 'home-outline',
  VACATION: 'airplane-outline',
  EMERGENCY_FUND: 'umbrella-outline',
  CUSTOM: 'flag-outline',
};

/** Chosen icon, or a sensible default by template for goals created before icons existed. */
export function goalIconOf(goal: Pick<SavingsGoal, 'icon' | 'template'>): IconName {
  if (goal.icon && goal.icon in Ionicons.glyphMap) return goal.icon as IconName;
  return TEMPLATE_ICONS[goal.template];
}

interface GoalIconPickerProps {
  value: string | null;
  onChange: (icon: string) => void;
}

/** 4×4 grid of selectable icon tiles; selected = navy tile + white glyph. */
export function GoalIconPicker({ value, onChange }: GoalIconPickerProps) {
  const { t } = useTranslation();
  return (
    <View style={{ gap: spacing.sm }}>
      {/* Owner: tester didn't know what the icons meant — they're decoration only. */}
      <AppText variant="labelMd" color={colors.outline}>
        {t('editGoal.iconHint')}
      </AppText>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm }}>
      {GOAL_ICONS.map((icon) => {
        const active = value === icon;
        return (
          <Pressable
            key={icon}
            accessibilityRole="button"
            accessibilityLabel={icon}
            onPress={() => onChange(icon)}
            style={{
              width: '22%',
              minWidth: 56,
              aspectRatio: 1.2,
              borderRadius: radius.card,
              backgroundColor: active ? colors.navy : colors.card,
              alignItems: 'center',
              justifyContent: 'center',
              flexGrow: 1,
            }}
          >
            <Ionicons name={icon} size={26} color={active ? '#FFFFFF' : colors.primary} />
          </Pressable>
        );
      })}
      </View>
    </View>
  );
}
